import React, { useEffect, useState, useCallback } from "react";
import { useVenue } from "@/components/shared/VenueContext";
import HosysAILayout from "@/components/hosysai/HosysAILayout";
import { toast } from "sonner";
import { getMemToken } from "@/api/api";
import { GraduationCap, Play, Check, Award, Loader2, Clock } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

async function apiCall(path, opts = {}) {
  const headers = { "Content-Type": "application/json", ...(opts.headers || {}) };
  const t = getMemToken();
  if (t) headers["Authorization"] = `Bearer ${t}`;
  const res = await fetch(`${API_URL}${path}`, { ...opts, credentials: "include", headers });
  if (!res.ok) throw new Error((await res.json()).detail || "Error");
  return res.json();
}

const STATUS_STYLE = {
  not_started: { label: "No iniciat", badge: "bg-slate-700 text-slate-300" },
  in_progress: { label: "En curs",    badge: "bg-amber-500/20 text-amber-300 border border-amber-500/30" },
  completed:   { label: "Completat",  badge: "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30" },
};

const LEVEL_LABELS = {
  beginner: "Bàsic",
  intermediate: "Intermedi",
  advanced: "Avançat",
};

export default function HosysAICourses() {
  const { currentVenue, loading: venueLoading } = useVenue();
  const venueId = currentVenue?.venue_id || currentVenue?.id;
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    try {
      const items = await apiCall(`/api/hosysai/my-courses?venue_id=${venueId}`);
      setCourses(items || []);
    } catch (e) {
      toast.error(e.message || "Error");
    } finally { setLoading(false); }
  }, [venueId]);

  useEffect(() => { load(); }, [load]);

  const startCourse = async (course) => {
    setBusy(course.course_id);
    try {
      await apiCall(`/api/hosysai/courses/${course.course_id}/start`, {
        method: "POST", body: JSON.stringify({ venue_id: venueId }),
      });
      toast.success(`Curs "${course.title}" iniciat`);
      load();
    } catch (e) { toast.error(e.message || "Error"); } finally { setBusy(null); }
  };

  const completeCourse = async (course) => {
    if (!window.confirm(`Marcar "${course.title}" com a completat i generar certificat?`)) return;
    setBusy(course.course_id);
    try {
      const cert = await apiCall(`/api/hosysai/courses/${course.course_id}/complete`, {
        method: "POST", body: JSON.stringify({ venue_id: venueId }),
      });
      toast.success(`Certificat generat: ${cert.certificate_id}`);
      load();
    } catch (e) { toast.error(e.message || "Error"); } finally { setBusy(null); }
  };

  return (
    <HosysAILayout>
      <div className="space-y-6" data-testid="hosysai-courses-page">
        <div>
          <p className="text-xs uppercase tracking-widest text-cyan-300 font-semibold">Els meus cursos</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mt-1">Formació en IA</h1>
          <p className="text-slate-400 text-sm mt-1">7 itineraris formatius per garantir el compliment de l'AI Act i l'ús responsable de la IA a la teva organització.</p>
        </div>

        {loading || venueLoading ? (
          <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {courses.map((c) => {
              const st = STATUS_STYLE[c.status] || STATUS_STYLE.not_started;
              return (
                <div key={c.course_id} className="p-5 rounded-2xl border border-white/10 bg-white/5 hover:border-cyan-400/30 transition-colors" data-testid={`course-${c.code}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-400/20 to-fuchsia-500/20 flex items-center justify-center border border-cyan-400/30">
                        <GraduationCap className="w-5 h-5 text-cyan-300" />
                      </div>
                      <div>
                        <p className="font-semibold text-white text-lg leading-tight">{c.title}</p>
                        <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                          <Clock className="w-3 h-3" /> {c.duration_min} min · {LEVEL_LABELS[c.level] || c.level}
                        </p>
                      </div>
                    </div>
                    <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${st.badge}`}>{st.label}</span>
                  </div>

                  {c.description && <p className="text-sm text-slate-400 mt-2 line-clamp-2">{c.description}</p>}

                  {c.status === "completed" && c.certificate_id && (
                    <div className="mt-3 flex items-center gap-2 text-xs text-emerald-300">
                      <Award className="w-3.5 h-3.5" />
                      <span className="font-mono">{c.certificate_id}</span>
                    </div>
                  )}

                  <div className="mt-4 flex items-center gap-2">
                    {c.status === "not_started" && (
                      <button
                        onClick={() => startCourse(c)}
                        disabled={busy === c.course_id}
                        className="px-4 py-2 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-200 border border-cyan-400/30 text-sm font-medium flex items-center gap-1 disabled:opacity-50"
                        data-testid={`start-${c.code}`}
                      >
                        {busy === c.course_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                        Començar
                      </button>
                    )}
                    {c.status === "in_progress" && (
                      <button
                        onClick={() => completeCourse(c)}
                        disabled={busy === c.course_id}
                        className="px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-400 to-cyan-500 text-white text-sm font-semibold flex items-center gap-1 disabled:opacity-50"
                        data-testid={`complete-${c.code}`}
                      >
                        {busy === c.course_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                        Marcar completat
                      </button>
                    )}
                    {c.status === "completed" && (
                      <span className="text-xs text-slate-500 italic">Completat el {(c.completed_at || "").slice(0, 10)}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="p-4 rounded-xl border border-cyan-400/20 bg-cyan-500/5 text-sm text-slate-300">
          <p className="font-semibold text-cyan-300 mb-1">Sub-fase 1B en construcció</p>
          Aquest és el llistat bàsic de cursos amb un flux simplificat (començar → completar → certificat). En propers releases s'afegirà: contingut multimèdia (vídeo, text, quizzes), exàmens amb preguntes, certificats PDF amb QR i URL pública `/verify/HAI-2026-XXXXX`.
        </div>
      </div>
    </HosysAILayout>
  );
}
